import React from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';

const HomePage = () => {
  const features = [
    {
      icon: '🧠',
      title: 'AI Running Coach',
      description: 'Get personalized advice and answers to your running questions from an AI coach trained on expert running knowledge. Ask about training, nutrition, form, and more.'
    },
    {
      icon: '📍',
      title: 'Smart Run Tracking',
      description: 'Track your runs with GPS precision. Monitor distance, pace, duration, calories burned, and see your route on an interactive map with mile markers.'
    },
    {
      icon: '📅',
      title: 'Custom Training Plans',
      description: 'Generate AI-powered training plans tailored to your goals, fitness level, and schedule. Perfect for 5K to marathon preparation with progress charts.'
    },
    {
      icon: '🏆',
      title: 'Race Predictions',
      description: 'Get accurate race time predictions for 5K, 10K, half marathon, and marathon distances based on your training data and performance.'
    },
    {
      icon: '🏁',
      title: 'Race Day Planner',
      description: 'AI-generated race day plans with pacing strategy, warmup routine, and nutrition timeline. Start your run directly from the plan and see a mile-by-mile comparison afterwards.'
    },
    {
      icon: '💪',
      title: 'Recovery Suggestions',
      description: 'Get personalized post-run recovery plans powered by AI — stretching routines, hydration tips, nutrition advice, and sleep recommendations tailored to your effort.'
    },
    {
      icon: '🎵',
      title: 'Music Integration',
      description: 'Control Apple Music right from your run screen. Play, pause, and skip tracks without leaving the app while you train.'
    },
    {
      icon: '🥗',
      title: 'Nutrition Tracking',
      description: 'Log your daily meals with manual entry or snap a photo and let AI analyze your food for calories and macronutrient breakdown.'
    },
    {
      icon: '🔄',
      title: 'Run Comparison',
      description: 'Compare your runs side by side. See how your pace, distance, and performance stack up against previous workouts to track improvement.'
    },
    {
      icon: '🌦️',
      title: 'Weather Integration',
      description: 'Automatic weather tracking during your runs. Know the conditions you ran in and how they affected your performance.'
    },
    {
      icon: '👥',
      title: 'Community Features',
      description: 'Connect with other runners, share your achievements, and find motivation in a supportive community of athletes.'
    },
    {
      icon: '📊',
      title: 'Detailed Analytics',
      description: 'View comprehensive run history, track your progress over time, and analyze your performance with detailed statistics and insights.'
    },
    {
      icon: '📱',
      title: 'Live Activities & Widget',
      description: 'Keep track of your run on your lock screen with iOS Live Activities and Dynamic Island. Plus a home screen widget showing your weekly stats.'
    },
    {
      icon: '🎯',
      title: 'Run Analysis',
      description: 'Get AI-powered analysis of your runs with insights on performance, improvements, and areas to focus on for your next training session.'
    },
    {
      icon: '🏅',
      title: 'Milestones & Achievements',
      description: 'Earn milestones as you hit personal records — longest run, fastest pace, streak goals, and more. Celebrate every step of your journey.'
    },
    {
      icon: '📣',
      title: 'Live Encouragement',
      description: 'Receive real-time motivational voice cues and encouragement during your runs to keep you pushing through every mile.'
    },
    {
      icon: '📶',
      title: 'Offline Mode',
      description: 'Track your runs without an internet connection. Your data syncs automatically when you\'re back online — never lose a run.'
    },
    {
      icon: '🔔',
      title: 'Smart Notifications',
      description: 'Intelligent reminders for weekly summaries, streak milestones, recovery windows, and weather alerts — timed so they\'re helpful, not annoying.'
    }
  ];

  const premiumFeatures = [
    {
      icon: '💬',
      title: 'Unlimited AI Chat',
      description: 'Ask your AI coach unlimited questions without any restrictions'
    },
    {
      icon: '📋',
      title: 'Unlimited Training Plans',
      description: 'Generate as many personalized training plans as you need'
    },
    {
      icon: '🏃',
      title: 'Race Predictions',
      description: 'Access AI-powered race time predictions for all distances'
    },
    {
      icon: '📈',
      title: 'Advanced Analytics',
      description: 'Deep dive into your performance with premium analytics and insights'
    },
    {
      icon: '🏁',
      title: 'Unlimited Race Day Plans',
      description: 'Generate unlimited race day plans with pacing, nutrition, and post-run comparison'
    },
    {
      icon: '🧬',
      title: 'AI Insights Dashboard',
      description: 'A comprehensive dashboard aggregating all your AI-powered insights in one place'
    },
    {
      icon: '🥗',
      title: 'AI Nutrition Analysis',
      description: 'Snap a photo of your meal and get instant calorie and macro breakdown powered by AI'
    },
    {
      icon: '🧠',
      title: 'Progressive Insights',
      description: 'Intelligence that evolves with you — insights deepen as you progress from beginner to advanced runner'
    }
  ];

  const steps = [
    {
      number: 1,
      title: 'Download & Sign Up',
      description: 'Get RunningAI Pro from the App Store and create your free account in seconds'
    },
    {
      number: 2,
      title: 'Start Running',
      description: 'Track your runs with GPS, monitor your stats, and build your running history'
    },
    {
      number: 3,
      title: 'Get AI Coaching',
      description: 'Chat with your AI coach, get personalized training plans, and receive expert guidance'
    },
    {
      number: 4,
      title: 'Achieve Your Goals',
      description: 'Track your progress, improve your performance, and reach your running goals faster'
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background"></div>
        <div className="hero-content">
          <div className="app-icon">🏃</div>
          <h1>RunningAI Pro</h1>
          <p className="subtitle">Your Personal AI-Powered Running Coach</p>
          <p className="description">
            Transform your running with AI-driven insights, personalized training plans, and real-time coaching
          </p>
          <a href="#features" className="cta-button">Discover Features</a>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="container">
          <h2 className="section-title">Powerful Features to Elevate Your Running</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Section */}
      <section className="premium">
        <div className="container">
          <h2 className="section-title">Premium Features</h2>
          <p className="premium-subtitle">Unlock unlimited potential with RunningAI Pro Premium</p>
          <div className="premium-grid">
            {premiumFeatures.map((feature, index) => (
              <div key={index} className="premium-feature">
                <h4>
                  <span className="premium-icon">{feature.icon}</span>
                  {feature.title}
                </h4>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps">
            {steps.map((step, index) => (
              <div key={index} className="step">
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>&copy; 2026 RunningAI Pro. All rights reserved.</p>
        <div className="footer-links">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/support">Support</Link>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
